// Demo story: monthly ESG series per org (seasonal curve + seeded noise) and
// per-branch shares. Overview = sum of months; by-branch = month x siteShare().
// The outlet's branches are its 6 sites; the recycler's are its 2 facilities.

import { DEMO_MATERIAL_IDS, materialById } from '../catalog';
import { DEMO_RECYCLER_ORG_ID } from '../constants';
import { monthKey, monthStart, rngFor, round, seasonal, type Rng } from '../prng';
import { DEMO_FACILITIES, DEMO_SITES } from './places';

export interface DemoEsgMonth {
  /** 'YYYY-MM' */
  month: string;
  /** All waste generated/handled = recycled streams + residual. */
  total_kg: number;
  /** Breakdown of total_kg by material id (residual under mixed municipal waste). */
  by_material_kg: Record<string, number>;
  recycled_kg: number;
  /** recycled_kg + residual sent to energy recovery instead of landfill. */
  landfill_diverted_kg: number;
  co2_avoided_t: number;
  pickups: number;
  water_m3: number;
  electricity_kwh: number;
  diesel_l: number;
  gas_m3: number;
  scope1_t: number;
  scope2_t: number;
  training_hours: number;
  hse_incidents: number;
}

interface EsgProfile {
  /** Base monthly kg per recycled material. */
  materials: Array<[string, number]>;
  /** Residual share of recycled kg, oldest month -> newest (improves over the year). */
  residualFrom: number;
  residualTo: number;
  kgPerPickup: number;
  electricity: [number, number];
  water: [number, number];
  diesel: number;
  gas: [number, number];
  refrigerant_t: [number, number];
  training: [number, number];
}

const M = DEMO_MATERIAL_IDS;

const OUTLET_PROFILE: EsgProfile = {
  materials: [
    [M.occ, 14000],
    [M.mixed_paper, 2600],
    [M.office_paper, 700],
    [M.pet, 2400],
    [M.hdpe, 1100],
    [M.ldpe_film, 2300],
    [M.aluminium_cans, 650],
    [M.uco, 900],
    [M.wood_pallets, 2100],
  ],
  residualFrom: 0.44,
  residualTo: 0.3,
  kgPerPickup: 1750,
  electricity: [430000, -110000],
  water: [2300, -350],
  diesel: 9200,
  gas: [1800, 250],
  refrigerant_t: [5.5, -1.5],
  training: [40, 96],
};

const RECYCLER_PROFILE: EsgProfile = {
  materials: [
    [M.occ, 62000],
    [M.mixed_paper, 18000],
    [M.office_paper, 6000],
    [M.newspaper, 5000],
    [M.pet, 14000],
    [M.hdpe, 7000],
    [M.ldpe_film, 9000],
    [M.pp, 6000],
    [M.aluminium_cans, 3500],
    [M.steel_scrap, 12000],
    [M.copper, 400],
    [M.clear_glass, 9000],
    [M.uco, 4000],
    [M.wood_pallets, 8000],
  ],
  residualFrom: 0.12,
  residualTo: 0.08,
  kgPerPickup: 2200,
  electricity: [185000, -35000],
  water: [900, -120],
  diesel: 21000,
  gas: [250, 40],
  refrigerant_t: [1.2, -0.3],
  training: [60, 130],
};

/** kg CO2e per litre diesel, per m3 natural gas, per kWh (Saudi grid). */
const DIESEL_KG_PER_L = 2.68;
const GAS_KG_PER_M3 = 1.93;
const GRID_KG_PER_KWH = 0.57;

function profileFor(orgId: string): EsgProfile {
  return orgId === DEMO_RECYCLER_ORG_ID ? RECYCLER_PROFILE : OUTLET_PROFILE;
}

function noise(rng: Rng, pct: number): number {
  return 1 + (rng.next() * 2 - 1) * pct;
}

function incidents(rng: Rng): number {
  const x = rng.next();
  if (x < 0.45) return 0;
  if (x < 0.8) return 1;
  if (x < 0.95) return 2;
  return 3;
}

/** One month; `offset` 0 = this month, -1 = last month … */
function esgMonth(orgId: string, offset: number): DemoEsgMonth {
  const p = profileFor(orgId);
  const start = monthStart(offset);
  const key = monthKey(start);
  const m = start.getUTCMonth();
  // One stream per org+calendar month: a month's numbers never depend on the window asked for.
  const rng = rngFor(`esg:${orgId}:${key}`);
  const age = Math.min(11, Math.max(0, -offset)); // 11 = oldest of the default window
  const growth = 1 + 0.012 * (11 - age);
  const season = seasonal(m, 1, 0.12);

  const by_material_kg: Record<string, number> = {};
  let recycled = 0;
  let co2 = 0;
  for (const [materialId, base] of p.materials) {
    const kg = Math.round(base * season * growth * noise(rng, 0.07));
    by_material_kg[materialId] = kg;
    recycled += kg;
    co2 += (kg / 1000) * (materialById(materialId)?.emission_factor_tco2e_per_t ?? 0);
  }
  const residualShare = p.residualTo + ((p.residualFrom - p.residualTo) * age) / 11;
  const residual = Math.round(recycled * residualShare * noise(rng, 0.05));
  by_material_kg[M.mixed_municipal] = (by_material_kg[M.mixed_municipal] ?? 0) + residual;

  const electricity_kwh = Math.round(seasonal(m, p.electricity[0], p.electricity[1]) * noise(rng, 0.04));
  const water_m3 = Math.round(seasonal(m, p.water[0], p.water[1]) * noise(rng, 0.05));
  const diesel_l = Math.round(p.diesel * seasonal(m, 1, 0.05) * growth * noise(rng, 0.06));
  const gas_m3 = Math.round(seasonal(m, p.gas[0], p.gas[1]) * noise(rng, 0.06));
  const refrigerant = seasonal(m, p.refrigerant_t[0], p.refrigerant_t[1]) * noise(rng, 0.1);

  return {
    month: key,
    total_kg: recycled + residual,
    by_material_kg,
    recycled_kg: recycled,
    landfill_diverted_kg: recycled + Math.round(residual * 0.1),
    co2_avoided_t: round(co2, 2),
    pickups: Math.max(1, Math.round(recycled / p.kgPerPickup)),
    water_m3,
    electricity_kwh,
    diesel_l,
    gas_m3,
    scope1_t: round((diesel_l * DIESEL_KG_PER_L + gas_m3 * GAS_KG_PER_M3) / 1000 + refrigerant, 2),
    scope2_t: round((electricity_kwh * GRID_KG_PER_KWH) / 1000, 2),
    training_hours: rng.int(p.training[0], p.training[1]),
    hse_incidents: incidents(rng),
  };
}

/**
 * The last `months` months for an org, oldest first, ending with the current
 * month (shown as a full month). The recycler org gets the recycler profile;
 * any other id gets the outlet profile.
 */
export function monthlyEsgSeries(orgId: string, months = 12): DemoEsgMonth[] {
  const n = Math.max(1, Math.floor(months));
  const out: DemoEsgMonth[] = [];
  for (let offset = -(n - 1); offset <= 0; offset += 1) out.push(esgMonth(orgId, offset));
  return out;
}

const SITE_SHARES: Record<string, number> = {
  [DEMO_SITES[0].id]: 0.24,
  [DEMO_SITES[1].id]: 0.16,
  [DEMO_SITES[2].id]: 0.14,
  [DEMO_SITES[3].id]: 0.22,
  [DEMO_SITES[4].id]: 0.13,
  [DEMO_SITES[5].id]: 0.11,
  [DEMO_FACILITIES[0].id]: 0.72,
  [DEMO_FACILITIES[1].id]: 0.28,
};

/**
 * Share (0..1) of its org's ESG totals that a branch accounts for. Outlet site
 * shares sum to 1, recycler facility shares sum to 1; unknown ids -> 0.
 */
export function siteShare(siteId: string): number {
  return SITE_SHARES[siteId] ?? 0;
}

/** Branch ids for an org's by-branch views: facilities for the recycler, sites otherwise. */
export function branchIdsFor(orgId: string): string[] {
  return orgId === DEMO_RECYCLER_ORG_ID ? DEMO_FACILITIES.map((f) => f.id) : DEMO_SITES.map((s) => s.id);
}
